import PropTypes from 'prop-types';
import React, { useState } from 'react';
import injectSheet from 'react-jss';

import { sendForgotPasswordEmail } from '~/api/login';
import {
  black2,
  evisortRed,
  evisortYellow,
  semibold,
} from '~/assets/shared-styles/general';
import LoginPageContainer from '~/components/LoginPage/LoginPageContainer/LoginPageContainer';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import { QueryParamType } from '~/enums/QueryParamType';
import { useAsync } from '~/hooks/useAsync';
import { useIntl } from 'react-intl';
import Alert from '~/ui/components/Alert/Alert';
import { isEmailValid } from '~/utils/helper.utils';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';

function Page(props) {
  const intl = useIntl();
  const { classes } = props;

  const workspace = getPageSearchQueryByKey(QueryParamType.Workspace);

  const [email, setEmail] = useState();
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState();

  const { executor: handleForgotPasswordEmail, isLoading } = useAsync(
    sendForgotPasswordEmail,
    { client: workspace, userId: email },
    {
      successHandler: () => {
        setEmailSent(true);
      },
      errorHandler: () => {
        setError(true);
      },
    },
  );

  return (
    <div>
      {emailSent ? (
        <LoginPageContainer
          classes={classes}
          title={intl.formatMessage({
            id: 'ForgotPasswordPage.checkInboxTitle',
            defaultMessage: 'Check your inbox',
            description: 'Title when email has been sent',
          })}
        >
          <Alert enableIcon variant="success">
            {intl.formatMessage({
              id: 'ForgotPasswordPage.emailSentMessage',
              defaultMessage:
                'You will receive reset link shortly. You may need to check your spam folder or unblock evisort@evisort.com',
              description: 'Success message after email is sent',
            })}
          </Alert>
        </LoginPageContainer>
      ) : (
        <LoginPageContainer
          classes={classes}
          title={intl.formatMessage({
            id: 'ForgotPasswordPage.resetPasswordTitle',
            defaultMessage: 'Reset password',
            description: 'Title for password reset page',
          })}
        >
          <div className={classes.contentCopy}>
            {intl.formatMessage({
              id: 'ForgotPasswordPage.instructionMessage',
              defaultMessage:
                'Enter your email address to reset your password. You may need to check your spam folder or unblock evisort@evisort.com',
              description: 'Instructions for password reset',
            })}
          </div>
          {error && (
            <div className={classes.errorMessage}>
              <Alert enableIcon variant="danger">
                {intl.formatMessage({
                  id: 'ForgotPasswordPage.errorMessage',
                  defaultMessage: 'An error ocurred. Please try again.',
                  description: 'Error message when request fails',
                })}
              </Alert>
            </div>
          )}
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <form onSubmit={handleForgotPasswordEmail}>
              <div>
                <input
                  autoFocus={true}
                  type="text"
                  value={email}
                  placeholder={intl.formatMessage({
                    id: 'ForgotPasswordPage.emailPlaceholder',
                    defaultMessage: 'Your email',
                    description: 'Placeholder text for email input',
                  })}
                  onChange={(e) => setEmail(e.target.value)}
                  className={classes.textInput}
                />
              </div>
              <div className={classes.formButtonContainer}>
                <button
                  type="submit"
                  className={classes.formButton}
                  onClick={handleForgotPasswordEmail}
                  disabled={!isEmailValid(email)}
                >
                  {intl.formatMessage({
                    id: 'ForgotPasswordPage.sendResetLinkButton',
                    defaultMessage: 'Send password reset link',
                    description: 'Button text to send password reset link',
                  })}
                </button>
              </div>
            </form>
          )}
        </LoginPageContainer>
      )}
    </div>
  );
}

Page.propTypes = {
  classes: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
};

const styles = {
  formButtonContainer: {
    marginTop: '20px',
  },
  formButton: {
    padding: '10px 20px',
    width: '360px',
    display: 'inline-block',
    backgroundColor: evisortYellow,
    border: 'solid 1px rgba(0, 0, 0, 0.25)',
    borderRadius: '3px',
    color: black2,
    fontSize: '15px',
    cursor: 'pointer',
    textDecoration: 'none',
    '&:disabled': {
      opacity: 0.5,
    },
  },
  loginFormLabel: {
    marginBottom: '8px',
    fontSize: '15px',
    fontWeight: semibold,
    textAlign: 'left',
  },
  textInput: {
    margin: '10px 0',
    padding: '10px',
    width: '360px',
    border: '1px solid #ccc',
    borderRadius: '3px',
    color: black2,
    fontSize: '16px',
    '&:placeholder': {
      opacity: 0.5,
    },
    '&:focus': {
      outline: 'none',
    },
  },
  contentCopy: {
    margin: '0 auto 30px',
    width: '360px',
    color: black2,
    fontSize: '14px',
    textAlign: 'left',
    lineHeight: '22px',
  },
  errorMessage: {
    margin: '20px',
    color: evisortRed,
    fontSize: '15px',
  },
};

export default injectSheet(styles)(Page);
